/* eslint-disable promise/param-names */
import i18n from '@/i18n/i18n'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { addMinutes, isAfter, isBefore, parseISO } from 'date-fns'
import { ElMessage, ElMessageBox } from 'element-plus'
import get from 'lodash/get'
import { mask } from 'maska'
import { getNotification } from './api'
import {
  ALLOWED_SERIAL_PREFIX,
  FIXED_PACKAGE_INFO_KEYS,
  IMAGES_EXTENSIONS,
  LOCAL_STORAGE_ITEM,
  MASKED_ADDRESS,
  MASKED_ANNOTATION,
  MASKED_PACKAGE_DIMENSION,
  OTHER_COUNTRY_LIST,
  QRCODE_BARCODE_TYPE_REGEX,
  trollyNumberLimitBT,
  trollyNumberLimitTP,
} from './constants'
import { ECustomFieldType, ECustomFieldUnitPosition, EDisplaySetting, EPackageInfoKey } from './enums'
import errorHandler from './errorHandler'
import { logError } from './errorLogger'
import { isEmpty } from './typeChecker'
import { ITrackingAuditData } from './types/audit'
import { IBarcode } from './types/barcodeTypes'
import {
  IGetTrackingResponse,
  ILabelDisplay,
  IListStringItem,
  IMessageType,
  INotificationsResponse,
} from './types/commonTypes'
import { ICustomFieldData, ISelectionItem } from './types/fieldData'
import { IActivationFields, IFieldSettings, IFormInputSchema } from './types/fieldDefinition'
import { IActivationCustomField, IProject, ITrackPointKeyVal } from './types/projectTypes'
// import { get } from 'lodash'
const t = i18n.global.t

export const observerClean = (value: unknown) => {
  if (!value) {
    return {}
  }

  return JSON.parse(JSON.stringify(value))
}

const maxMessage = 3
const messageArr: string[] = []

export function openMessage(message: string, type: IMessageType, duration = 3000, messageParams?: object) {
  if (messageArr.length === maxMessage) return message
  messageArr.push(message)

  const messageInstance = ElMessage({
    showClose: true,
    center: true,
    message,
    duration,
    type,
    ...messageParams,
    onClose: () => {
      messageArr.shift()
    },
  })

  setTimeout(() => {
    const messageEl = document.querySelector('.el-message:last-of-type') as HTMLElement
    if (messageEl) {
      messageEl.style.cursor = 'pointer'
      messageEl.addEventListener(
        'click',
        () => {
          messageInstance.close()
        },
        { once: true }
      )
    }
  }, 0)

  // Automatically log errors and warnings
  if (type === 'error' || type === 'warning') {
    logError(message, {}, type, message)
  }
}

export function validateEmail(email: string): boolean {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  return regex.test(email?.toLowerCase())
}

export const deepSearch = (object: object, key: string, predicate: Function): object | null => {
  const newObj = object as Record<string, object>
  if (Object.prototype.hasOwnProperty.call(object, key) && predicate(key, newObj[key]) === true) {
    return object
  }

  for (let i = 0; i < Object.keys(object).length; i++) {
    const value = newObj[Object.keys(object)[i]] as Record<string, object>
    if (typeof value === 'object' && value != null) {
      const o = deepSearch(value, key, predicate)
      if (o != null) return o
    }
  }

  return null
}

type Obj = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function searchValueByKey(obj: Obj, key: string): any {
  for (const prop in obj) {
    if (prop === key) {
      return obj[prop]
    } else if (typeof obj[prop] === 'object') {
      const result = searchValueByKey(obj[prop], key)
      if (result !== undefined) {
        return result
      }
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function findValueByPath(obj: Obj, path: string): any {
  const keys = path.split('.')
  let result = obj
  for (const key of keys) {
    result = result[key]
    if (result === undefined) {
      return undefined
    }
  }
  return result
}

export const isAllObjectValueEmpty = (obj: object): boolean => {
  const temp: Record<string, unknown> = { ...obj }
  return !Object.keys(temp).find((key) => {
    return !isEmpty(temp[key])
  })
}

export const getDefaultValueOfData = (data: unknown) => {
  if (typeof data === 'number') {
    return 0
  }
  if (typeof data === 'string') {
    return ''
  }
  if (typeof data === 'boolean') {
    return false
  }
  if (typeof data === 'object') {
    if (Object.prototype.toString.call(data) === '[object Array]') {
      return []
    }
    return {}
  }
}

export const addEmptyActivationData = (activateData: object, originActivationBarcode: object): object => {
  const cloneActivationData = observerClean(activateData)
  const cloneOriginActivationBarcode = observerClean(originActivationBarcode)
  return Object.keys(activateData).reduce((acc, key) => {
    if (isEmpty(cloneActivationData?.[key])) {
      if (isEmpty(cloneOriginActivationBarcode?.[key])) {
        return acc
      }
      return { ...acc, [key]: getDefaultValueOfData(cloneOriginActivationBarcode?.[key]) }
    }
    if (!isAllObjectValueEmpty(cloneActivationData?.[key])) {
      return { ...acc, [key]: cloneActivationData?.[key] }
    }
    const data = {
      ...acc,
      [key]: addEmptyActivationData(cloneActivationData?.[key], cloneOriginActivationBarcode?.[key]),
    } as Record<string, unknown>
    if (isEmpty(data[key])) {
      delete data[key]
    }
    return data
  }, {})
}

export const isJson = (item: string) => {
  item = typeof item !== 'string' ? JSON.stringify(item) : item

  try {
    item = JSON.parse(item)
  } catch (e) {
    return false
  }

  if (typeof item === 'object' && item !== null) {
    return true
  }

  return false
}

export const filterEmptyActivateData = (activateData: object): object => {
  const cloneObj = observerClean(activateData)
  return Object.keys(activateData).reduce((acc, key) => {
    if (isEmpty(cloneObj[key])) {
      return acc
    }
    if (!isAllObjectValueEmpty(cloneObj[key])) {
      return { ...acc, [key]: cloneObj[key] }
    }
    return { ...acc, ...filterEmptyActivateData(cloneObj[key]) }
  }, {})
}

export const removeEmpty = (obj: { [x: string]: unknown }) => {
  Object.keys(obj).forEach((k) => !obj[k] && obj[k] !== undefined && delete obj[k])
  return obj
}

export const toSnakeCase = (str: string) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function sortTrackingPoints(trackPointArr: ITrackPointKeyVal[]) {
  trackPointArr.sort((a, b) => {
    return a.value.order && b.value.order ? a.value.order - b.value.order : Number(a.key) - Number(b.key)
  })
}

export async function getVisitorId() {
  let visitorId = ''
  visitorId = localStorage.getItem(LOCAL_STORAGE_ITEM.VISITOR_ID) || ''
  if (visitorId) {
    return visitorId
  } else {
    visitorId = (await FingerprintJS.load().then((fp) => fp.get())).visitorId
    localStorage.setItem(LOCAL_STORAGE_ITEM.VISITOR_ID, visitorId)
    return visitorId
  }
}

export async function promptVideoPermission() {
  await navigator.mediaDevices
    .getUserMedia({ video: true })
    .then(async (stream) => {
      stream.getVideoTracks().forEach((track) => track.stop())
    })
    .catch((e) => {
      errorHandler(e)
    })
}

export function stopStreams(activeStreams?: MediaStream[]) {
  if (activeStreams) {
    activeStreams.forEach((stream) => {
      stream.getTracks().forEach((track) => track.stop()) // Stop all tracks
    })

    activeStreams.length = 0
  }
}

export function dataURLtoFile(dataURI: string) {
  try {
    if (isEmpty(dataURI) && dataURI.split(',').length < 2) return

    const byteString = atob(dataURI.split(',')[1])
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: dataURI.split(',')[0].split(';')[0] })
  } catch (error) {
    return null
  }
}

export function getFilePathFromURL(string: string) {
  if (!string) return ''
  if (string.includes('blob:')) return string

  const start = string.indexOf('Enterprise') || string.indexOf('Barcode')
  const end = string.indexOf('?')

  return string.slice(start, end)
}

export function getCurrentUtcOffset() {
  const date = new Date()
  return date.getTimezoneOffset() / -60
}

export function makeid(len: number) {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  let counter = 0
  while (counter < len) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

interface IResizeImageOptions {
  maxSize: number
  file: File
}
export function resizeImage({ maxSize, file }: IResizeImageOptions) {
  const image = new Image()
  const canvas = document.createElement('canvas')
  const dataURItoBlob = (dataURI: string) => {
    const bytes =
      dataURI.split(',')[0].indexOf('base64') >= 0 ? atob(dataURI.split(',')[1]) : unescape(dataURI.split(',')[1])
    const mime = dataURI
      .split(',')[0]
      .split(':')[1]
      .split(';')[0]
    const max = bytes.length
    const ia = new Uint8Array(max)
    for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i)
    return new Blob([ia], { type: mime })
  }
  const resize = () => {
    let width = image.width
    let height = image.height

    if (width > height) {
      if (width > maxSize) {
        height *= maxSize / width
        width = maxSize
      }
    } else {
      if (height > maxSize) {
        width *= maxSize / height
        height = maxSize
      }
    }
    if (canvas) {
      canvas.width = width
      canvas.height = height
      // eslint-disable-next-line no-unused-expressions
      canvas?.getContext('2d')?.drawImage(image, 0, 0, width, height)
      const dataUrl = canvas.toDataURL('image/jpeg')
      return dataURItoBlob(dataUrl)
    }
  }

  return new Promise((ok, no) => {
    image.onload = () => ok(resize())

    if (!file.type.match(/image.*/)) {
      no(new Error('Not an image'))
      return
    }

    const reader = new FileReader()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reader.onload = (evt: any) => (image.src = evt.target.result)
    reader.readAsDataURL(file)
  })
}

export function getFirstCustomActiveField(
  customValues: Record<string, ICustomFieldData>,
  customFields: Record<string, IFormInputSchema>
) {
  let label = ''
  if (!isEmpty(customValues)) {
    Object.keys(customValues).forEach((key) => {
      const value = customValues[key]
      const fSettings = customFields[key]
      if (!fSettings) return
      let val = ''
      if (value.fieldType === ECustomFieldType.SINGLE_SELECT) {
        val = value.singleSelect?.value || ''
      } else if (value.fieldType === ECustomFieldType.MULTI_SELECT) {
        val =
          value?.multiSelect
            ?.reduce((acc, curr) => {
              acc.push(curr?.value || '')
              return acc
            }, [] as string[])
            .join(', ') || ''
      } else if (value.fieldType) {
        val = value.text || value.number?.toString() || value.date || ''
      }

      if (FIXED_PACKAGE_INFO_KEYS.includes(key)) {
        switch (key) {
          case EPackageInfoKey.TRACKING_NUMBER:
            label = `${fSettings.label || t('tracking_number')}: ${value}`
            break
          case EPackageInfoKey.EXTERNAL_ID:
            label = `${t('externalId')}: ${value}`
            break
          default:
            label = ''
            break
        }
      }
      if (val) {
        label = `${fSettings.label}: ${val}`
      }
    })
  }
  return label
}

export function getValueFromCustomFields(
  flattenCustomValue: Record<string, ICustomFieldData>,
  flattenCustomFields: Record<string, IFormInputSchema>,
  path: string
): string {
  const splitedKey = path.split('.')
  const lastKey = splitedKey[splitedKey.length - 1]
  const fieldType = flattenCustomFields?.[lastKey]?.fieldType || ''

  if (!fieldType) {
    return ''
  }

  if (fieldType === ECustomFieldType.TEXT) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue?.[lastKey]?.[ECustomFieldType.TEXT]}`
  }
  if (fieldType === ECustomFieldType.NUMBER) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue?.[lastKey]?.[
      ECustomFieldType.NUMBER
    ]?.toLocaleString()}`
  }
  if (fieldType === ECustomFieldType.ADJUSTMENT) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue?.[lastKey]?.[
      ECustomFieldType.ADJUSTMENT
    ]?.toLocaleString()}`
  }
  if (fieldType === ECustomFieldType.PHONE_NUMBER) {
    return `${flattenCustomFields[lastKey].label}: ${
      flattenCustomValue?.[lastKey]?.[ECustomFieldType.PHONE_NUMBER]?.number
    }`
  }
  if (fieldType === ECustomFieldType.DATE) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue?.[lastKey]?.[ECustomFieldType.DATE]}`
  }
  if (fieldType === ECustomFieldType.EMAIL) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue?.[lastKey]?.[ECustomFieldType.EMAIL]}`
  }

  if (fieldType === ECustomFieldType.REFERENCE) {
    const value =
      flattenCustomValue?.[lastKey]?.[ECustomFieldType.REFERENCE]?.text ||
      flattenCustomValue?.[lastKey]?.[ECustomFieldType.REFERENCE]?.template
    return `${flattenCustomFields[lastKey].label}: ${value}`
  }
  if (fieldType === ECustomFieldType.SINGLE_SELECT) {
    const selectionItem = (flattenCustomValue?.[lastKey]?.[ECustomFieldType.SINGLE_SELECT] as unknown) as ISelectionItem
    if (selectionItem?.value) {
      return `${flattenCustomFields[lastKey].label}: ${selectionItem.value}`
    }
  }
  if (fieldType === ECustomFieldType.MULTI_SELECT) {
    const multiSelect = (flattenCustomValue?.[lastKey]?.[ECustomFieldType.MULTI_SELECT] as unknown) as ISelectionItem[]
    if (multiSelect) {
      const valueOfMultiSelect = multiSelect
        .reduce((acc, curr) => {
          acc.push(curr.value)
          return acc
        }, [] as string[])
        .join(',')
      return `${flattenCustomFields[lastKey].label}: ${valueOfMultiSelect}`
    }
  }

  if (fieldType === ECustomFieldType.CALCULATION) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue[fieldType]?.calculation ?? ''}`
  }

  if (flattenCustomValue[fieldType]) {
    return `${flattenCustomFields[lastKey].label}: ${flattenCustomValue[fieldType]}`
  }
  return ''
}

export function flattenCustomFields(obj: Record<string, Record<string, unknown>>): Record<string, unknown> {
  const flattenObj = Object.keys(obj || {}).reduce((acc, cur) => {
    // exclude nextTrackPointNotification now
    if (cur === 'nextTrackPointNotification') return acc

    if (cur.match(/^custom[a-zA-Z]*Fields/g)) return { ...acc, ...obj[cur] }

    if (cur === ECustomFieldType.LOCATION) {
      return { ...acc, [cur]: { ...obj[cur], fieldType: ECustomFieldType.LOCATION } }
    }

    if (cur === ECustomFieldType.ESIGN) {
      return { ...acc, [cur]: { ...obj[cur], fieldType: ECustomFieldType.ESIGN } }
    }

    return { ...acc, [cur]: obj[cur] }
  }, {})
  return flattenObj
}

export function getDynamicLabel(
  flattenFields: Record<string, IFieldSettings>,
  flattenValue: Record<string, ICustomFieldData>,
  pathKey: string
) {
  const processedpathKey = pathKey.replace(/^activationData\./, '')
  let label = ''
  const fixedPackageInfo = [
    {
      value: 'activationData.trackingNumber',
      title: ` ${flattenFields?.trackingNumber?.label || t('tracking_number')}`,
    },
    {
      value: 'activationData.externalId',
      title: ` ${t('externalId')}`,
    },
    {
      value: 'activationData.dimension.height',
      title: `${t('height')}`,
    },
    {
      value: 'activationData.dimension.width',
      title: `${t('width')}`,
    },
    {
      value: 'activationData.dimension.depth',
      title: `${t('depth')}`,
    },
    {
      value: 'activationData.dimension.weight',
      title: `${t('weight')}`,
    },
  ]

  // Search label in fixed fields
  fixedPackageInfo.forEach((element) => {
    if (element.value === pathKey) {
      const title = element?.title || ''

      const value = findValueByPath(flattenValue, processedpathKey) || ''
      label = `${title}: ${value}`
    }
  })

  return (
    label || // Stop If found label in fixed package info.
    getValueFromCustomFields(flattenValue, flattenFields, pathKey) ||
    getFirstCustomActiveField(flattenValue, flattenFields) ||
    ''
  )
}

export function isHideVisibilityConfig(visibility: string, isAuth: boolean, defaultVisibility = true): boolean {
  if (EDisplaySetting.VISIBLE === visibility) return false

  // If the value is unknown then will following the defaultVisibility logic.
  // If defaultVisibility = true it will determ as not hidden
  return visibility === EDisplaySetting.UNKNOWN
    ? !defaultVisibility
    : visibility === EDisplaySetting.HIDDEN || (visibility === EDisplaySetting.VISIBLE_WHEN_LOGGED_IN && !isAuth)
}

export function mobileCheck() {
  const toMatch = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i]

  return toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem))
}

export function isFirefoxOrOperaAgent() {
  const ua = navigator.userAgent
  const isOpera = ua.indexOf('OPR') !== -1
  const isFirefox = ua.indexOf('Firefox') !== -1 && ua.indexOf('OPR') === -1
  return isOpera || isFirefox
}

function getBarcodeListConfigFromType(bc: IBarcode, project: IProject) {
  return `${project?.barcodeTypes?.[bc?.barcodeType]?.uiConfig?.listViewTitleLabelField}` || ''
}

function barcodeTypeInfoActivationFields(barcodeType: string, project: IProject): IActivationFields {
  return project?.barcodeTypes?.[barcodeType]?.activationFields || {}
}

export function addLabelToBarcode(barcodes: IBarcode[], project: IProject) {
  barcodes.forEach((val) => {
    if (!val) return
    const flattenValue = flattenCustomFields(
      (val?.activationData as unknown) as Record<string, Record<string, unknown>>
    ) as Record<string, ICustomFieldData>
    const flattenField = flattenCustomFields(
      (barcodeTypeInfoActivationFields(val?.barcodeType || '', project) as unknown) as Record<
        string,
        Record<string, unknown>
      >
    ) as Record<string, IFieldSettings>
    const label = getDynamicLabel(flattenField, flattenValue, getBarcodeListConfigFromType(val, project))
    Object.assign(val, {
      label,
    })
  })
}

export function getProjectCode(project: string) {
  return project.split('@')[0]
}

export const isQrCode = (plainText: string) => !!plainText.match(QRCODE_BARCODE_TYPE_REGEX)

export const getBarcodeIdFromQrCode = (plainText: string) => plainText.split('/').pop()
export const shouldHandleAsNewBarcode = (
  plainText: string,
  previousScannedText: string,
  previousScannedBarcodeId: string
) => {
  if (!plainText) {
    return false
  }
  if (isQrCode(plainText)) {
    const barcodeId = getBarcodeIdFromQrCode(plainText) || ''
    if (barcodeId === previousScannedBarcodeId) {
      return false
    }
  }
  if (previousScannedText) {
    return plainText !== previousScannedText
  }
  return true
}

export const filterNotification = (result: INotificationsResponse, currentDate: Date) => {
  return (
    result?.notifications?.filter((val) => {
      const displayedDate = parseISO(val.displayedDateTime)
      if (!val.mobileAlertDurationByMinutes) {
        return isAfter(currentDate, displayedDate)
      }
      if (
        val.mobileAlertDurationByMinutes &&
        isAfter(currentDate, displayedDate) &&
        isBefore(currentDate, addMinutes(displayedDate, val.mobileAlertDurationByMinutes))
      ) {
        return true
      } else {
        return false
      }
    }) || []
  )
}

export const displayTargetAlert = async (resultScanned: IBarcode) => {
  const notifications: INotificationsResponse = await getNotification(resultScanned.id)
  const currentDate = parseISO(new Date().toISOString())
  const filteredResult = filterNotification(notifications, currentDate)
  if (filteredResult.length > 0) {
    const message = filteredResult.map((val) => val.body).join('\n--------------------------------\n')
    await ElMessageBox.confirm(message, '', {
      confirmButtonText: t('ok'),
      confirmButtonClass: 'danger',
      cancelButtonText: t('skip'),
    }).catch((e: Error) => {
      throw e
    })
  }
}

export function objectToArray<T>(data: Record<string, T>, prefixKey?: string) {
  const arr: { key: string, value: T }[] = []
  Object.entries(data).map(([key, value]) => {
    let customKey = key
    if (prefixKey) {
      customKey = key.indexOf(prefixKey) > -1 ? key : `${prefixKey}${key}`
    }

    arr.push({ key: customKey, value })
  })

  return arr
}

interface Version {
  major: number
  minor: number
  patch: number
  versionString: string
}

export function parseVersion(version: string): Version {
  const parts = version.split('.')
  const major = parseInt(parts[0], 10)
  const minor = parseInt(parts[1], 10)
  const patch = parseInt(parts[2], 10)
  const versionString = version.toString()

  if ([major, minor, patch].some(isNaN)) {
    throw new Error('Invalid version number')
  }

  return { major, minor, patch, versionString }
}

export function exhaustiveCheck(exhaustiveCheck: never): never {
  throw new Error(`Unhandled case: ${exhaustiveCheck}`)
}

export function shouldUpdateClientVersion(currentVersionString: string, serverVersionString: string): boolean {
  try {
    const currentVersion = parseVersion(currentVersionString)
    const serverVersion = parseVersion(serverVersionString)

    if (serverVersion.major > currentVersion.major) {
      return true
    } else if (serverVersion.major < currentVersion.major) {
      return false
    }

    // Major versions are equal, compare minor versions
    if (serverVersion.minor > currentVersion.minor) {
      return true
    } else if (serverVersion.minor < currentVersion.minor) {
      return false
    }

    // Minor versions are equal, compare patch versions
    return serverVersion.patch > currentVersion.patch
  } catch (error) {
    return false
  }
}

function compileOriginData(barcode: IBarcode) {
  if (barcode?.activationData?.maskedFields?.includes('origin')) {
    barcode.activationData.origin = MASKED_ADDRESS
  }
}
function compileDestinationData(barcode: IBarcode) {
  if (barcode?.activationData?.maskedFields?.includes('origin')) {
    barcode.activationData.destination = MASKED_ADDRESS
  }
}

function compilePackageInfoData(barcode: IBarcode) {
  if (barcode.activationData?.maskedFields?.includes('dimension')) {
    barcode.activationData.dimension = {
      ...MASKED_PACKAGE_DIMENSION,
    }
  }

  if (barcode.activationData?.maskedFields?.includes('trackingNumber')) {
    barcode.activationData.trackingNumber = MASKED_ANNOTATION
  }

  if (barcode.activationData?.maskedFields?.includes('externalId')) {
    barcode.activationData.externalId = MASKED_ANNOTATION
  }
}

function compileCustomFieldData(customFields: unknown, maskedFields: string[], definition: unknown) {
  if (maskedFields) {
    const keys = maskedFields.filter((maskField) => maskField.match(/^customFields\./))
    keys.forEach((pathKey) => {
      const field: IFormInputSchema = get(definition, pathKey)
      const key = pathKey.split('.')[pathKey.split('.').length - 1]
      if (!customFields) return
      switch (field?.fieldType) {
        case ECustomFieldType.TEXT:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.TEXT,
              [ECustomFieldType.TEXT]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.DATE:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.DATE,
              [ECustomFieldType.DATE]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.EMAIL:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.EMAIL,
              [ECustomFieldType.EMAIL]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.PHONE_NUMBER: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.PHONE_NUMBER,
              [ECustomFieldType.PHONE_NUMBER]: {
                countryCode: MASKED_ANNOTATION,
                number: MASKED_ANNOTATION,
              },
            },
          })
          break
        }
        case ECustomFieldType.NUMBER:
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.NUMBER,
              [ECustomFieldType.NUMBER]: MASKED_ANNOTATION,
            },
          })
          break
        case ECustomFieldType.MULTI_SELECT: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.MULTI_SELECT,
              [ECustomFieldType.MULTI_SELECT]: [{ key: MASKED_ANNOTATION, value: MASKED_ANNOTATION }],
            },
          })
          break
        }
        case ECustomFieldType.FILES: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.FILES,
              [ECustomFieldType.FILES]: [{ name: MASKED_ANNOTATION, path: MASKED_ANNOTATION, url: MASKED_ANNOTATION }],
            },
          })
          break
        }
        case ECustomFieldType.SINGLE_SELECT: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.SINGLE_SELECT,
              [ECustomFieldType.SINGLE_SELECT]: { key: MASKED_ANNOTATION, value: MASKED_ANNOTATION },
            },
          })
          break
        }
        case ECustomFieldType.REFERENCE: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.REFERENCE,
              [ECustomFieldType.REFERENCE]: {
                template: MASKED_ANNOTATION,
                [ECustomFieldType.TEXT]: MASKED_ANNOTATION,
              },
            },
          })
          break
        }
        case ECustomFieldType.ESIGN: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.ESIGN,
              [ECustomFieldType.ESIGN]: MASKED_ANNOTATION,
            },
          })
          break
        }
        case ECustomFieldType.GHG_EMISSION: {
          Object.assign(customFields, {
            [key]: {
              fieldType: ECustomFieldType.GHG_EMISSION,
              [ECustomFieldType.GHG_EMISSION]: MASKED_ANNOTATION,
            },
          })
          break
        }
      }
    })
  }
}

function compileActivationDataCustomFields(barcode: IBarcode, projectDetail: IProject) {
  const currentBarcodeDefinition = projectDetail?.barcodeTypes[barcode?.barcodeType]
  if (!currentBarcodeDefinition) return
  compileCustomFieldData(
    barcode.activationData.customFields,
    barcode.activationData.maskedFields || [],
    currentBarcodeDefinition.activationFields
  )
  // eslint-disable-next-line no-unused-expressions
  barcode.activationData.linkedBarcodes?.forEach((barcode) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    compileLinkedData(barcode, projectDetail)
  })
}

function compileTrackingDataAudit(maskedFields: string[], auditData?: ITrackingAuditData) {
  if (auditData && maskedFields) {
    if (maskedFields.includes('trackedUserName')) {
      auditData.trackedUserName = MASKED_ANNOTATION
    }
    if (maskedFields.includes('trackedUserId')) {
      auditData.trackedUserId = MASKED_ANNOTATION
    }
  }
}

function compileTrackingData(barcode: IBarcode, projectDetail: IProject) {
  const trackingData = barcode?.trackingData || []
  trackingData.forEach((td, idx) => {
    const currentTPKey = td?.currentTrackPointKey
    const nextTPKey = td?.nextTrackPointKey
    const tpDefinition = projectDetail?.trackPoints?.[currentTPKey]?.trackPointForms?.[nextTPKey]
    compileCustomFieldData(td?.customFields, td?.maskedFields as string[], tpDefinition)
    compileTrackingDataAudit(td?.maskedFields as string[], td?.audit)
    // eslint-disable-next-line no-unused-expressions
    trackingData?.[idx]?.linkedBarcodes?.forEach((barcode) => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      compileLinkedData(barcode, projectDetail)
    })
  })
}

export function getCompiledBarcode(barcode: IBarcode, project: IProject) {
  compileOriginData(barcode)
  compileDestinationData(barcode)
  compilePackageInfoData(barcode)
  compileActivationDataCustomFields(barcode, project)
  compileTrackingData(barcode, project)
  return barcode
}

function compileLinkedData(linkedBarcodes: IBarcode, project: IProject) {
  if (!linkedBarcodes) return
  getCompiledBarcode(linkedBarcodes, project)
}

export function maskPhoneNum(num: string, countryCode: string) {
  if (!countryCode) {
    return num
  }
  const countryData = OTHER_COUNTRY_LIST[countryCode as keyof typeof OTHER_COUNTRY_LIST]
  const countPhoneNumberDigits = (formatString: string) => (formatString.match(/[#\d]/g) || []).length
  const masks = countryData.mask.sort((e1, e2) => countPhoneNumberDigits(e1) - countPhoneNumberDigits(e2))
  const selectedMask =
    masks.length === 1
      ? masks[0]
      : masks.find((mask: string) => countPhoneNumberDigits(num) <= countPhoneNumberDigits(mask)) ??
        masks[masks.length - 1]

  return mask(num, selectedMask)
}

export function parseCustomFieldsValue(
  customFields: Record<string, ILabelDisplay>,
  customFieldsDefinition: Record<string, IActivationCustomField>,
  barcode: IBarcode
) {
  Object.entries(customFieldsDefinition)
    .sort((a, b) => (a?.[1]?.order && b?.[1]?.order ? a[1].order - b[1].order : 1))
    .forEach(([key, customField]) => {
      const customFieldItem = barcode?.activationData?.customFields?.[key]
      if (customFieldItem && customField.label) {
        if (customField.isHidden || !customField.label) return
        customFields[key] = {
          value: '',
          label: customField.label,
          fieldType: customField.fieldType,
        }
        switch (customField.fieldType) {
          case ECustomFieldType.TEXT:
            if (customFieldItem?.text) {
              customFields[key].value = customFieldItem.text
            }
            break
          case ECustomFieldType.DATE:
            if (customFieldItem?.date) {
              customFields[key].value = customFieldItem.date
            }
            break
          case ECustomFieldType.DATE_TIME_DATA:
            if (customFieldItem?.dateTimeData) {
              customFields[key].value = customFieldItem.dateTimeData
            }
            break
          case ECustomFieldType.LIST_STRING:
            if (customFieldItem?.listString) {
              customFields[key].value = customFieldItem.listString
            }
            break
          case ECustomFieldType.EMAIL:
            if (customFieldItem?.email) {
              customFields[key].value = customFieldItem.email
            }
            break
          case ECustomFieldType.PHONE_NUMBER: {
            if (customFieldItem?.phoneNumber) {
              const phoneNo = customFieldItem.phoneNumber
              const dialCode =
                OTHER_COUNTRY_LIST[phoneNo!.countryCode as keyof typeof OTHER_COUNTRY_LIST]?.dialCode ?? ''

              customFields[key].value =
                phoneNo.number === MASKED_ANNOTATION
                  ? MASKED_ANNOTATION
                  : `${dialCode} ${maskPhoneNum(phoneNo.number, phoneNo!.countryCode)}`
            }
            break
          }
          case ECustomFieldType.NUMBER:
            if (customFieldItem?.number) {
              customFields[key].value = parseFloat(customFieldItem.number.toString()).toLocaleString()
            }
            if (
              customField?.position &&
              customField?.position !== ECustomFieldUnitPosition.UNKNOWN &&
              customField.unitType
            ) {
              if (customField.position === ECustomFieldUnitPosition.PREFIX) {
                customFields[key].value = `${customField.unitType} ${customFields[key].value}`
              } else if (customField.position === ECustomFieldUnitPosition.SUFFIX) {
                customFields[key].value = `${customFields[key].value} ${customField.unitType}`
              }
            }
            if (customFieldItem.number?.toString() === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          case ECustomFieldType.ADJUSTMENT:
            if (customFieldItem?.currentValue !== null && customFieldItem?.currentValue !== undefined) {
              customFields[key].value = parseFloat(customFieldItem.currentValue.toString()).toLocaleString()
            }
            if (
              customField?.position &&
              customField?.position !== ECustomFieldUnitPosition.UNKNOWN &&
              customField.unitType
            ) {
              if (customField.position === ECustomFieldUnitPosition.PREFIX) {
                customFields[key].value = `${customField.unitType} ${customFields[key].value}`
              } else if (customField.position === ECustomFieldUnitPosition.SUFFIX) {
                customFields[key].value = `${customFields[key].value} ${customField.unitType}`
              }
            }
            if (customFieldItem.currentValue?.toString() === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          case ECustomFieldType.MULTI_SELECT: {
            const multiSelect = customFieldItem?.multiSelect as ISelectionItem[]
            if (multiSelect) {
              const data: string[] = []
              multiSelect.forEach((element) => {
                if (element.key === MASKED_ANNOTATION) {
                  data.push(MASKED_ANNOTATION)
                } else {
                  data.push(customField.selections ? customField?.selections?.[element?.key]?.label : '')
                }
              })
              customFields[key].value = data.toString()
            }
            break
          }
          case ECustomFieldType.SINGLE_SELECT: {
            const selections = customFieldItem?.[ECustomFieldType.SINGLE_SELECT]
            if (selections && customField?.selections?.[selections?.key]?.label) {
              customFields[key].value = customField?.selections?.[selections?.key]?.label
            }
            if (selections?.key === MASKED_ANNOTATION) {
              customFields[key].value = MASKED_ANNOTATION
            }
            break
          }
          case ECustomFieldType.REFERENCE: {
            const referencedData = customFieldItem?.[ECustomFieldType.REFERENCE]?.text
            customFields[key].value = referencedData || ''
            break
          }
          case ECustomFieldType.CALCULATION: {
            const calculationData = customFieldItem?.[ECustomFieldType.CALCULATION]
            customFields[key].value = calculationData ?? ''
          }
        }
      }
    })
}
export function arrayToObject<T>(
  data: Array<{
    key: string
    value: T
  }>
) {
  const object: Record<string, T> = {}
  data.forEach((item) => (object[item.key] = item.value))

  return object
}

export function setCookie(cname: string, cvalue: string, exdays: number) {
  const d = new Date()
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000)
  const expires = 'expires=' + d.toUTCString()
  document.cookie = cname + '=' + cvalue + ';' + 'domain=.smartbarcode.app;path=/;' + expires + ';path=/'
}

export function getCookie(cname: string) {
  const name = cname + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

export function deleteCookie(cname: string) {
  document.cookie = cname + '=; expires=Thu, 01-Jan-70 00:00:01 GMT; path=/; domain=.smartbarcode.app;'
}

export function isImagesFile(fullPath: string): boolean {
  // eslint-disable-next-line no-useless-catch
  try {
    const ext = fullPath.substring(fullPath.lastIndexOf('.')).toLowerCase()

    return IMAGES_EXTENSIONS.includes(ext)
  } catch (error) {
    throw error
  }
}

export function formatDateTimeListString(dateTime?: string): string {
  if (!dateTime) return ''
  const date = new Date(dateTime)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${month}-${day} ${hours}:${minutes}:${seconds}`
}

export function isDataNotEmpty(data: unknown): boolean {
  if (data === null || data === undefined) {
    return false
  }

  if (typeof data === 'string') {
    return data.trim() !== ''
  }

  if (typeof data === 'number') {
    return true
  }

  if (Array.isArray(data)) {
    return data.length > 0 && data.some((item) => isDataNotEmpty(item))
  }

  if (typeof data === 'object' && data !== null) {
    const keys = Object.keys(data)
    if (keys.length === 0) {
      return false
    }

    return keys.some((key) => isDataNotEmpty((data as Record<string, unknown>)[key]))
  }

  return false
}

export const mergeTrackingDataWithSerials = (response: IGetTrackingResponse) => {
  if (!response?.trackingData?.customFields?.sirial?.listString || !response?.serials) {
    return response
  }

  const listString = response.trackingData.customFields.sirial.listString
  const serials = response.serials

  const serialsMap = new Map(serials.map((serial: IListStringItem) => [serial.id, serial]))

  response.trackingData.customFields.sirial.listString = listString.map((item: IListStringItem) => {
    const matchingSerial = serialsMap.get(item.id)
    return matchingSerial || item
  })

  return response
}

export const trollyNumberLimit = (serials: IListStringItem[]): number => {
  if (serials[0]?.code?.startsWith(ALLOWED_SERIAL_PREFIX.TP)) {
    return trollyNumberLimitTP
  } else {
    return trollyNumberLimitBT
  }
}

export const generateLotNumberFromDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const year = date
    .getFullYear()
    .toString()
    .slice(2) // Get last 2 digits of year
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
